// Shared custom-field validation used by BOTH the line-item editor and the
// create/edit ticket form, so the rules never drift between them.
//
// The "kind" of a field is derived from its schema fieldType, with a key-name
// fallback so date/url/email/mpan/phone fields still validate even without a type.

import { isValidPhoneNumber, PHONE_INVALID_MESSAGE } from '@/lib/phone';

export type FieldKind = 'date' | 'url' | 'email' | 'number' | 'mpan' | 'phone' | 'text';

// Key fragments that mark a phone field typed as plain text (e.g. "Contact
// Number"). Matched against the key with separators removed. Deliberately no
// bare "tel" — it would catch "hotelCode".
const PHONE_KEY_PARTS = ['phone', 'mobile', 'contactnumber', 'contactno', 'telephone', 'cellnumber'];

const URL_RE = /^https?:\/\/\S+$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * "Missing" for a required field. Shared so the record column's pointer and the
 * Line Items tab's REQUIRED markers can never disagree about what counts.
 */
export const isEmptyValue = (v: unknown): boolean =>
  v === undefined || v === null || String(v).trim() === '';

/** How many of `fields` are required and unset in `values`. */
export function countMissingRequired(
  fields: { key: string; required?: boolean }[],
  values: Record<string, unknown>,
): number {
  return fields.filter((f) => f.required && isEmptyValue(values?.[f.key])).length;
}

export function getFieldKind(key: string, type?: string): FieldKind {
  const k = (key || '').toLowerCase();
  // MPAN is a 13-digit UK electricity meter id — keyed, and wins over a generic
  // number/text type so it always gets the 13-digit rule.
  if (k.includes('mpan')) return 'mpan';
  const t = (type || '').toLowerCase();
  if (t === 'date' || t === 'datetime') return 'date';
  if (t === 'url') return 'url';
  if (t === 'email') return 'email';
  if (t === 'number' || t === 'decimal' || t === 'currency') return 'number';
  if (t === 'phone' || t === 'tel') return 'phone';
  // Key-name fallback only for free-text fields: a select/textarea/checkbox
  // whose key mentions "phone" must keep its own behaviour.
  const isFreeText = t === '' || t === 'text' || t === 'string';
  const compact = k.replace(/[^a-z0-9]/g, '');
  if (isFreeText && PHONE_KEY_PARTS.some((part) => compact.includes(part))) return 'phone';
  if (k.includes('date')) return 'date';
  if (k.includes('url') || k.includes('link')) return 'url';
  if (k.includes('email')) return 'email';
  if (k.includes('eac') || k.includes('aq')) return 'number';
  return 'text';
}

/** Error message for a non-empty value that fails its kind; null when valid.
 *  Emptiness is handled separately by the required-field check. */
export function validateFieldValue(kind: FieldKind, value: string): string | null {
  const v = (value ?? '').trim();
  if (!v) return null;
  switch (kind) {
    case 'url':
      return URL_RE.test(v) ? null : 'Enter a valid URL (https://…)';
    case 'email':
      return EMAIL_RE.test(v) ? null : 'Enter a valid email address';
    case 'date':
      return Number.isNaN(Date.parse(v)) ? 'Enter a valid date' : null;
    case 'number':
      return Number.isNaN(Number(v)) ? 'Enter a valid number' : null;
    case 'mpan':
      // 13 digits (spaces allowed for readability, e.g. "20 0000 1234 567").
      return /^\d{13}$/.test(v.replace(/\s+/g, '')) ? null : 'MPAN must be 13 digits';
    case 'phone':
      return isValidPhoneNumber(v) ? null : PHONE_INVALID_MESSAGE;
    default:
      return null;
  }
}

export const htmlInputType = (kind: FieldKind): string =>
  kind === 'date' ? 'date' : kind === 'number' ? 'number' : kind === 'email' ? 'email' : kind === 'url' ? 'url' : kind === 'phone' ? 'tel' : 'text';
